import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Button, DataTable, Input } from "../../../components";
import { AppRoutes } from "../../../constants";
import useDebounce from "../../../hooks/useDebounce";
import {
  getOrganizations_V2,
  updateOrganization_V2,
} from "../../../Services_V2/Organization.service";
import { ExtOrganizationType } from "../../../types";
import { showError } from "../../../utils/error";
import { organizationColumns } from "./column";
import styles from "./organization-list.module.css";
import { mobileText } from "../../../utils/responsiveStyle";

const OrganizationList = () => {
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<ExtOrganizationType[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 0-based for DataTable
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getPayload = useCallback(
    () => ({
      page: currentPage + 1, // API expects 1-based
      limit: rowsPerPage,
      search: debouncedSearchTerm,
    }),
    [currentPage, rowsPerPage, debouncedSearchTerm],
  );

  const fetchOrganizations = useCallback(async () => {
    try {
      const payload = getPayload();
      const res = await getOrganizations_V2(payload);
      setOrganizations(res?.data ?? []);
      setTotal(res?.total ?? 0);
    } catch (error) {
      showError(error);
    }
  }, [getPayload]);

  useEffect(() => {
    fetchOrganizations();
  }, [currentPage, rowsPerPage, debouncedSearchTerm]);

  const handleActiveChange = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      organization: ExtOrganizationType,
    ) => {
      const checked = event.target.checked;
      try {
        await updateOrganization_V2(organization._id, { isActive: checked });
        toast.success(
          checked ? "Organization activated" : "Organization inactivated",
        );

        setOrganizations((prev) =>
          prev.map((o) =>
            o._id === organization._id ? { ...o, isActive: checked } : o,
          ),
        );
      } catch (error) {
        showError(error);
      }
    },
    [],
  );

  const handleEdit = useCallback(
    (org: ExtOrganizationType) => {
      navigate(AppRoutes.ADMIN.ORGANIZATIONS.UPDATE(org._id));
    },
    [navigate],
  );

  const columns = useMemo(() => {
    return organizationColumns(currentPage, rowsPerPage, handleActiveChange);
  }, [currentPage, rowsPerPage, handleActiveChange]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p style={{ fontWeight: 500 }}>Total {total} Organizations added</p>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => navigate(AppRoutes.ADMIN.ORGANIZATIONS.ADD)}
          >
            Organization
          </Button>
        </Box>
      </div>

      <Box sx={{ mt: 4 }}>
        <DataTable
          data={organizations}
          columns={columns}
          total={total}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          showPagination
          onPageChange={(_, page) => setCurrentPage(page)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setCurrentPage(0);
          }}
          onEdit={(row) => handleEdit(row)}
          tableCellSx={{
            py: 0.3,
            ...mobileText,
            lineHeight: 1.2,
          }}
        />
      </Box>
    </div>
  );
};

export default OrganizationList;
